// utils/BalanceSheetReport.js
import React, { useEffect } from "react";

const formatAmount = (value) => {
  const num = Number(value) || 0;
  const formatted = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `(${formatted})` : formatted;
};

const formatAccountName = (name) => {
  if (!name) return "";
  return name
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const BalanceSheetReport = ({
  data,
  companyName = "Your Company Name",
  reportTitle = "Balance Sheet",
  periodLabel = "",
  preparedBy = "",
  onAfterPrint,
}) => {
  // Auto-print on mount
  useEffect(() => {
    if (!data) return;
    const timer = setTimeout(() => window.print(), 300);
    return () => clearTimeout(timer);
  }, [data]);

  // Return after print dialog closes
  useEffect(() => {
    const handleAfterPrint = () => {
      if (typeof onAfterPrint === "function") onAfterPrint();
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [onAfterPrint]);

  if (!data) return null;

  return (
    <div className="bs-report">
      <style>{`
        @media print {
            @page {
              size: A4;
              margin: 10mm 12mm;
            }
            body * { visibility: hidden; }
            .bs-report, .bs-report * { visibility: visible; }
            .bs-report {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              font-size: 10pt;
            }
            .no-print { display: none !important; }
          }
          
          .bs-report {
            font-family: "Segoe UI", Arial, Helvetica, sans-serif;
            color: #111;
            max-width: 210mm;
            margin: 0 auto;
            padding: 12px 16px;
            background: #fff;
            line-height: 1.25;
          }
          
          .bs-header {
            text-align: center;
            margin-bottom: 12px;
            border-bottom: 2px solid #222;
            padding-bottom: 8px;
          }
          .bs-header h1 { margin: 0 0 2px 0; font-size: 16pt; font-weight: 700; }
          .bs-header h2 { margin: 0 0 4px 0; font-size: 12pt; font-weight: 600; color: #333; }
          .bs-header .period { font-size: 9pt; color: #555; }
          
          .bs-table { width: 100%; border-collapse: collapse; margin-bottom: 2px; }
          .bs-table td { padding: 2px 4px; vertical-align: top; }
          .bs-table .account { padding-left: 12px; }
          .bs-table .amount {
            text-align: right;
            white-space: nowrap;
            font-variant-numeric: tabular-nums;
            width: 120px;
          }
          .bs-table .section-title {
            font-weight: 700;
            font-size: 10pt;
            padding-top: 8px;
            padding-bottom: 2px;
            border-bottom: 1px solid #999;
          }
          .bs-table .total-row td {
            font-weight: 700;
            border-top: 1px solid #333;
            padding-top: 4px;
          }
          .bs-table .grand-row td {
            font-weight: 700;
            font-size: 10.5pt;
            border-top: 2px solid #111;
            border-bottom: 2px solid #111;
            padding-top: 6px;
            padding-bottom: 6px;
          }
          .bs-table .negative { color: #b91c1c; }
          
          .bs-footer {
            margin-top: 20px;
            font-size: 8pt;
            color: #555;
            display: flex;
            justify-content: space-between;
          }
          .bs-balanced {
            margin-top: 10px;
            text-align: center;
            font-size: 9pt;
            font-weight: 600;
          }
          .bs-balanced.ok { color: #15803d; }
          .bs-balanced.bad { color: #b91c1c; }
      `}</style>

      {/* HEADER */}
      <div className="bs-header">
        <h1>{companyName}</h1>
        <h2>{reportTitle}</h2>
        {periodLabel && <div className="period">As of {periodLabel}</div>}
      </div>

      <table className="bs-table">
        <tbody>
          {/* ========== ASSETS ========== */}
          <tr>
            <td className="section-title" colSpan={2}>
              Assets
            </td>
          </tr>
          {(data.assetRows || []).map((row, i) => (
            <tr key={row.accountId ?? `asset-${i}`}>
              <td className="account">{formatAccountName(row.accountName)}</td>
              <td className={`amount ${row.amount < 0 ? "negative" : ""}`}>
                {formatAmount(row.amount)}
              </td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Total Assets</td>
            <td className={`amount ${data.totalAssets < 0 ? "negative" : ""}`}>
              {formatAmount(data.totalAssets)}
            </td>
          </tr>

          {/* ========== LIABILITIES ========== */}
          <tr>
            <td className="section-title" colSpan={2}>
              Liabilities
            </td>
          </tr>
          {(data.liabilityRows || []).map((row, i) => (
            <tr key={row.accountId ?? `liab-${i}`}>
              <td className="account">{formatAccountName(row.accountName)}</td>
              <td className={`amount ${row.amount < 0 ? "negative" : ""}`}>
                {formatAmount(row.amount)}
              </td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Total Liabilities</td>
            <td
              className={`amount ${
                data.totalLiabilities < 0 ? "negative" : ""
              }`}
            >
              {formatAmount(data.totalLiabilities)}
            </td>
          </tr>

          {/* ========== EQUITY ========== */}
          <tr>
            <td className="section-title" colSpan={2}>
              Owner's Equity
            </td>
          </tr>
          {(data.equityRows || []).map((row, i) => (
            <tr key={row.accountId ?? `eq-${i}`}>
              <td className="account">{formatAccountName(row.accountName)}</td>
              <td className={`amount ${row.amount < 0 ? "negative" : ""}`}>
                {formatAmount(row.amount)}
              </td>
            </tr>
          ))}
          <tr className="total-row">
            <td>Total Equity</td>
            <td className={`amount ${data.totalEquity < 0 ? "negative" : ""}`}>
              {formatAmount(data.totalEquity)}
            </td>
          </tr>

          {/* ========== GRAND TOTAL ========== */}
          <tr className="grand-row">
            <td>Total Liabilities & Equity</td>
            <td
              className={`amount ${
                data.totalLiabilitiesAndEquity < 0 ? "negative" : ""
              }`}
            >
              {formatAmount(data.totalLiabilitiesAndEquity)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* BALANCED STATUS */}
      <div className={`bs-balanced ${data.equationBalanced ? "ok" : "bad"}`}>
        {data.equationBalanced
          ? "✓ Accounting Equation Balanced (Assets = Liabilities + Equity)"
          : "✗ Accounting Equation Not Balanced"}
      </div>

      {/* FOOTER */}
      <div className="bs-footer">
        <div>
          {preparedBy && <div>Prepared by: {preparedBy}</div>}
          <div>Generated on: {new Date().toLocaleDateString("en-GB")}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div>Page 1 of 1</div>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetReport;
